// Embudos · detalle: estado, propietario y pasos ordenados con métricas y % de paso a paso.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, ChevronDown, ExternalLink, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Loading, Select } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { StatusToggle, Thumb } from "../landings/shared.tsx";
import { STEP_ICON, STEP_LABEL } from "./shared.ts";

type StepType = "landing" | "form" | "survey" | "pipeline";
interface Step {
  id: string; type: StepType; refId: string; name?: string;
  title: string; thumb: string | null; status: string | null; url: string | null; missing: boolean;
  metrics: { entered: number; converted: number; enteredLabel: string; convertedLabel: string; extra?: Record<string, number> };
  stepRate: number | null; conversionRate: number;
}
interface Funnel { id: string; name: string; status: "draft" | "published"; ownerId: string | null; ownerName: string | null; steps: Step[] }
interface Options {
  landings: Array<{ id: string; name: string; status: string }>; forms: Array<{ id: string; name: string; status: string }>;
  surveys: Array<{ id: string; name: string; status: string }>; pipelines: Array<{ id: string; name: string }>; users: Array<{ id: string; name: string }>;
}

const EXTRA_LABEL: Record<string, string> = { visitas: "Visitas", abiertas: "Abiertas", ganadas: "Ganadas", perdidas: "Perdidas", abiertasEmbudo: "Abiertas del embudo" };

function stepLink(s: Step): string | null {
  if (s.missing) return null;
  if (s.type === "landing") return `/marketing/landings/${s.refId}`;
  if (s.type === "form") return `/marketing/formularios/${s.refId}`;
  if (s.type === "survey") return `/marketing/encuestas/${s.refId}`;
  return "/deals";
}

export function FunnelDetail(): JSX.Element {
  const { id = "" } = useParams();
  const me = useMe();
  const admin = Boolean(me.data?.admin);
  const qc = useQueryClient();
  const q = useQuery<Funnel>({ queryKey: ["mkt-funnel", id], queryFn: () => api.get(`/mkt/funnels/${id}`) });
  const opts = useQuery<Options>({ queryKey: ["mkt-funnel-options"], queryFn: () => api.get("/mkt/funnels/options"), enabled: admin });
  const [name, setName] = useState("");
  const [addType, setAddType] = useState<StepType>("landing");
  const [addRef, setAddRef] = useState("");
  useEffect(() => { if (q.data) setName(q.data.name); }, [q.data]);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/mkt/funnels/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["mkt-funnel", id] }); void qc.invalidateQueries({ queryKey: ["mkt-funnels"] }); },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudo cargar el embudo." onRetry={() => void q.refetch()} />;
  const f = q.data;
  const raw = (steps: Step[]): Array<{ id: string; type: StepType; refId: string; name?: string }> =>
    steps.map((s) => ({ id: s.id, type: s.type, refId: s.refId, ...(s.name ? { name: s.name } : {}) }));
  const saveSteps = (steps: Step[]): void => patch.mutate({ steps: raw(steps) });
  const move = (i: number, d: -1 | 1): void => {
    const arr = [...f.steps]; const j = i + d; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j]!, arr[i]!]; saveSteps(arr);
  };
  const choices = !opts.data ? [] : addType === "landing" ? opts.data.landings : addType === "form" ? opts.data.forms : addType === "survey" ? opts.data.surveys : opts.data.pipelines;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Link to=".." relative="path" className="btn-ghost btn-sm" aria-label="Volver"><ArrowLeft size={16} /></Link>
          {admin
            ? <Input value={name} maxLength={160} className="max-w-md font-medium" aria-label="Nombre del embudo" onChange={(e) => setName(e.target.value)}
                onBlur={() => { if (name.trim() && name.trim() !== f.name) patch.mutate({ name: name.trim() }); }} />
            : <h2 className="text-lg font-semibold text-ink truncate">{f.name}</h2>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StatusToggle published={f.status === "published"} disabled={!admin || patch.isPending} onChange={(v) => patch.mutate({ status: v ? "published" : "draft" })} />
          {admin && opts.data ? (
            <Select value={f.ownerId ?? ""} className="w-auto" aria-label="Propietario" onChange={(e) => patch.mutate({ ownerId: e.target.value || null })}>
              <option value="">Sin propietario</option>
              {opts.data.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </Select>
          ) : <span className="text-sm text-ink-secondary">Propietario: {f.ownerName ?? "—"}</span>}
        </div>
      </div>

      {!f.steps.length && <Card><EmptyState title="El embudo no tiene pasos" body="Añade landings, formularios, encuestas o un pipeline de ventas en el orden en que los recorre el contacto." /></Card>}

      <div className="space-y-0">
        {f.steps.map((s, i) => {
          const Icon = STEP_ICON[s.type];
          const link = stepLink(s);
          return (
            <div key={s.id}>
              {i > 0 && (
                <div className="flex items-center gap-2 pl-10 py-1.5 text-xs text-ink-muted">
                  <ChevronDown size={16} />
                  {s.stepRate != null ? <span><span className="font-semibold text-ink tnum">{s.stepRate}%</span> pasa de «{f.steps[i - 1]!.title}» a este paso</span> : <span>—</span>}
                </div>
              )}
              <Card className={`p-4 ${s.missing ? "opacity-60" : ""}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="w-7 h-7 rounded-full bg-primary-soft text-primary grid place-items-center text-sm font-semibold shrink-0">{i + 1}</span>
                    {s.type === "landing" ? <Thumb src={s.thumb} /> : <div className="w-20 h-14 rounded-md border border-line bg-primary-soft grid place-items-center shrink-0"><Icon size={22} strokeWidth={1.5} className="text-primary" /></div>}
                    <div className="min-w-0">
                      <div className="text-xs text-ink-muted flex items-center gap-1"><Icon size={12} /> {STEP_LABEL[s.type]}</div>
                      <div className="font-medium text-ink truncate">{link ? <Link className="hover:underline" to={link}>{s.title}</Link> : s.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {s.status && <Badge color={s.status === "published" ? "green" : "gray"}>{s.status === "published" ? "Publicado" : "Borrador"}</Badge>}
                        {s.missing && <Badge color="red">Elemento eliminado</Badge>}
                        {s.url && s.status === "published" && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs link inline-flex items-center gap-1"><ExternalLink size={11} /> Ver</a>}
                      </div>
                    </div>
                  </div>
                  {!s.missing && (
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div><div className="dt-label">{s.metrics.enteredLabel}</div><div className="text-lg font-semibold text-ink tnum">{s.metrics.entered}</div></div>
                      <div><div className="dt-label">{s.metrics.convertedLabel}</div><div className="text-lg font-semibold text-ink tnum">{s.metrics.converted}</div></div>
                      <div><div className="dt-label">Conversión</div><div className="text-lg font-semibold text-primary tnum">{s.conversionRate}%</div></div>
                      {s.metrics.extra && (
                        <div className="text-xs text-ink-muted self-center space-y-0.5">
                          {Object.entries(s.metrics.extra).map(([k, v]) => <div key={k}>{EXTRA_LABEL[k] ?? k}: <span className="tnum text-ink-secondary">{v}</span></div>)}
                        </div>
                      )}
                    </div>
                  )}
                  {admin && (
                    <div className="flex sm:flex-col gap-1 shrink-0">
                      <button className="btn-ghost btn-sm" aria-label="Subir" disabled={i === 0 || patch.isPending} onClick={() => move(i, -1)}><ArrowUp size={14} /></button>
                      <button className="btn-ghost btn-sm" aria-label="Bajar" disabled={i === f.steps.length - 1 || patch.isPending} onClick={() => move(i, 1)}><ArrowDown size={14} /></button>
                      <button className="btn-ghost btn-sm text-danger" aria-label="Quitar paso" disabled={patch.isPending} onClick={() => saveSteps(f.steps.filter((x) => x.id !== s.id))}><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      {admin && (
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <Field label="Tipo de paso">
              <Select value={addType} onChange={(e) => { setAddType(e.target.value as StepType); setAddRef(""); }}>
                {(["landing", "form", "survey", "pipeline"] as const).map((t) => <option key={t} value={t}>{STEP_LABEL[t]}</option>)}
              </Select>
            </Field>
            <div className="flex-1">
              <Field label="Elemento">
                <Select value={addRef} onChange={(e) => setAddRef(e.target.value)}>
                  <option value="">— Elige —</option>
                  {choices.map((c) => <option key={c.id} value={c.id}>{c.name}{"status" in c && c.status !== "published" ? " (borrador)" : ""}</option>)}
                </Select>
              </Field>
            </div>
            <Button variant="primary" disabled={!addRef || patch.isPending || f.steps.length >= 20}
              onClick={() => { saveSteps([...f.steps, { id: Math.random().toString(36).slice(2, 10), type: addType, refId: addRef } as Step]); setAddRef(""); }}>
              <Plus size={16} /> Añadir paso
            </Button>
          </div>
          <p className="text-xs text-ink-muted mt-2">% de paso a paso = entradas del paso ÷ conversiones del paso anterior. En el pipeline se cuentan las oportunidades de contactos que se convirtieron en las landings o formularios de este embudo.</p>
        </Card>
      )}
    </div>
  );
}
